/**
 * PromoCodeAnalytics - Marketing Analytics Dashboard
 * 
 * Displays campaign performance, channel analytics, and A/B test results
 * SuperAdmin only access
 */

import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAdminAuth } from '../auth/useAdminAuth';
import { API_ENDPOINTS, buildApiUrl } from '../config/api-endpoints';
import './PromoCodeAnalytics.css';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PromoCodeAnalytics = () => {
  const { user: currentUser } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [promoCodes, setPromoCodes] = useState([]);
  const [dateRange, setDateRange] = useState('30'); // 7, 30, 90, or 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const isSuperAdmin = currentUser?.isSuperAdmin || false;

  // ── New analytics state ──────────────────────────────────────────────────
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);
  const [subscriptionBreakdown, setSubscriptionBreakdown] = useState(null);
  const [downgradeAnalysis, setDowngradeAnalysis] = useState(null);
  const [campaignROI, setCampaignROI] = useState(null);

  /**
   * Fetch pre-aggregated analytics from promo-analytics-query Lambda.
   * Falls back gracefully if the Lambda URL is not yet configured.
   */
  const fetchAnalytics = useCallback(async () => {
    const baseUrl = API_ENDPOINTS.ADMIN.PROMO_ANALYTICS;
    if (!baseUrl) return; // Lambda not yet deployed — skip silently

    try {
      setAnalyticsLoading(true);
      setAnalyticsError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const params = new URLSearchParams();
      if (dateRange !== 'all' && dateRange !== 'custom') {
        const days = parseInt(dateRange);
        const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        params.set('startDate', start);
      } else if (dateRange === 'custom' && customStartDate && customEndDate) {
        params.set('startDate', customStartDate);
        params.set('endDate', customEndDate);
      }

      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      const qs = params.toString() ? `?${params}` : '';

      const [breakdownRes, downgradeRes, roiRes] = await Promise.all([
        fetch(buildApiUrl(baseUrl, `/subscription-breakdown${qs}`), { headers }),
        fetch(buildApiUrl(baseUrl, `/downgrade-analysis${qs}`), { headers }),
        fetch(buildApiUrl(baseUrl, `/campaign-roi${qs}`), { headers })
      ]);

      if (breakdownRes.ok) setSubscriptionBreakdown(await breakdownRes.json());
      if (downgradeRes.ok) setDowngradeAnalysis(await downgradeRes.json());
      if (roiRes.ok) setCampaignROI(await roiRes.json());
    } catch (err) {
      console.error('[PromoCodeAnalytics] Analytics fetch error:', err);
      setAnalyticsError(err.message);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [dateRange, customStartDate, customEndDate]);

  // Fetch promo codes data
  const fetchPromoCodes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      const response = await fetch(`${API_ENDPOINTS.ADMIN.PROMO_CODE}/admin/promo-codes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data.promoCodes || []);
        setLastUpdate(new Date());
      } else {
        throw new Error(`Failed to fetch promo codes: ${response.status}`);
      }
    } catch (err) {
      console.error('[PromoCodeAnalytics] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchPromoCodes();
      fetchAnalytics();
    }
  }, [isSuperAdmin, fetchPromoCodes, fetchAnalytics]);

  // Filter codes by date range
  const filterByDateRange = useCallback((codes) => {
    if (!dateRange || dateRange === 'all') return codes;

    const now = new Date();
    let startDate;

    if (dateRange === 'custom') {
      if (!customStartDate || !customEndDate) return codes;
      startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      return codes.filter(c => {
        const createdDate = new Date(c.createdAt);
        return createdDate >= startDate && createdDate <= endDate;
      });
    }

    const days = parseInt(dateRange);
    startDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return codes.filter(c => new Date(c.createdAt) >= startDate);
  }, [dateRange, customStartDate, customEndDate]);

  // Calculate campaign metrics
  const getCampaignMetrics = useCallback(() => {
    const filteredCodes = filterByDateRange(promoCodes);
    const campaigns = {};

    filteredCodes.forEach(code => {
      const campaign = code.campaignName || 'No Campaign';
      if (!campaigns[campaign]) {
        campaigns[campaign] = {
          name: campaign,
          totalCodes: 0,
          totalRedemptions: 0,
          codes: []
        };
      }
      campaigns[campaign].totalCodes++;
      campaigns[campaign].totalRedemptions += (code.usageCount || 0);
      campaigns[campaign].codes.push(code);
    });

    return Object.values(campaigns).map(c => ({
      ...c,
      conversionRate: c.totalCodes > 0 ? ((c.totalRedemptions / c.totalCodes) * 100).toFixed(2) : 'N/A'
    })).sort((a, b) => {
      const rateA = parseFloat(a.conversionRate) || 0;
      const rateB = parseFloat(b.conversionRate) || 0;
      return rateB - rateA;
    });
  }, [promoCodes, filterByDateRange]);

  // Calculate channel metrics
  const getChannelMetrics = useCallback(() => {
    const filteredCodes = filterByDateRange(promoCodes);
    const channels = {};

    filteredCodes.forEach(code => {
      const channel = code.channel || 'other';
      if (!channels[channel]) {
        channels[channel] = {
          name: channel,
          totalCodes: 0,
          totalRedemptions: 0
        };
      }
      channels[channel].totalCodes++;
      channels[channel].totalRedemptions += (code.usageCount || 0);
    });

    return Object.values(channels).map(c => ({
      ...c,
      conversionRate: c.totalCodes > 0 ? ((c.totalRedemptions / c.totalCodes) * 100).toFixed(2) : '0.00'
    })).sort((a, b) => parseFloat(b.conversionRate) - parseFloat(a.conversionRate));
  }, [promoCodes, filterByDateRange]);

  // Calculate A/B test metrics with chi-squared test
  const getABTestMetrics = useCallback(() => {
    const filteredCodes = filterByDateRange(promoCodes);
    const campaigns = {};

    // Group by campaign and A/B test group
    filteredCodes.forEach(code => {
      if (!code.campaignName || !code.abTestGroup) return;
      
      const key = code.campaignName;
      if (!campaigns[key]) {
        campaigns[key] = {
          campaignName: code.campaignName,
          variants: {}
        };
      }

      const variant = code.abTestGroup;
      if (!campaigns[key].variants[variant]) {
        campaigns[key].variants[variant] = {
          name: variant,
          totalCodes: 0,
          totalRedemptions: 0
        };
      }

      campaigns[key].variants[variant].totalCodes++;
      campaigns[key].variants[variant].totalRedemptions += (code.usageCount || 0);
    });

    // Calculate conversion rates and statistical significance
    return Object.values(campaigns).map(campaign => {
      const variants = Object.values(campaign.variants).map(v => ({
        ...v,
        conversionRate: v.totalCodes > 0 ? ((v.totalRedemptions / v.totalCodes) * 100).toFixed(2) : '0.00'
      }));

      // Chi-squared test for statistical significance
      let significance = 'N/A';
      let winningVariant = null;

      if (variants.length >= 2) {
        const hasEnoughData = variants.every(v => v.totalRedemptions >= 30);
        
        if (hasEnoughData) {
          // Simple chi-squared test implementation
          const chiSquared = calculateChiSquared(variants);
          const pValue = chiSquaredToPValue(chiSquared, variants.length - 1);
          significance = pValue < 0.05 ? 'Significant' : 'Not Significant';
          
          // Find winning variant (highest conversion rate with significance)
          if (pValue < 0.05) {
            winningVariant = variants.reduce((max, v) => 
              parseFloat(v.conversionRate) > parseFloat(max.conversionRate) ? v : max
            );
          }
        } else {
          significance = 'Insufficient data';
        }
      }

      return {
        campaignName: campaign.campaignName,
        variants: variants.sort((a, b) => parseFloat(b.conversionRate) - parseFloat(a.conversionRate)),
        significance,
        winningVariant
      };
    });
  }, [promoCodes, filterByDateRange]);

  // Simple chi-squared calculation
  const calculateChiSquared = (variants) => {
    const totalCodes = variants.reduce((sum, v) => sum + v.totalCodes, 0);
    const totalRedemptions = variants.reduce((sum, v) => sum + v.totalRedemptions, 0);
    const expectedRate = totalRedemptions / totalCodes;

    let chiSquared = 0;
    variants.forEach(v => {
      const expected = v.totalCodes * expectedRate;
      const observed = v.totalRedemptions;
      chiSquared += Math.pow(observed - expected, 2) / expected;
    });

    return chiSquared;
  };

  // Convert chi-squared to p-value (simplified approximation)
  const chiSquaredToPValue = (chiSquared, degreesOfFreedom) => {
    // Simplified p-value approximation for chi-squared test
    // For df=1: critical value at p=0.05 is 3.841
    // For df=2: critical value at p=0.05 is 5.991
    const criticalValues = { 1: 3.841, 2: 5.991, 3: 7.815, 4: 9.488 };
    const critical = criticalValues[degreesOfFreedom] || 3.841;
    return chiSquared > critical ? 0.04 : 0.06; // Simplified: < 0.05 or > 0.05
  };

  // Render functions
  const renderOverview = () => {
    const campaigns = getCampaignMetrics();
    
    return (
      <div className="promo-analytics__overview">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Campaign Overview</h3>
            <div className="promo-analytics__filters">
              <select 
                className="promo-analytics__select"
                value={dateRange}
                onChange={e => setDateRange(e.target.value)}
              >
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
                <option value="custom">Custom range</option>
                <option value="all">All time</option>
              </select>
              
              {dateRange === 'custom' && (
                <div className="promo-analytics__date-inputs">
                  <input 
                    type="date" 
                    className="promo-analytics__input"
                    value={customStartDate}
                    onChange={e => setCustomStartDate(e.target.value)}
                  />
                  <span>to</span>
                  <input 
                    type="date" 
                    className="promo-analytics__input"
                    value={customEndDate}
                    onChange={e => setCustomEndDate(e.target.value)}
                  />
                </div>
              )}
            </div>
          </div>

          {campaigns.length === 0 ? (
            <p className="promo-analytics__empty">No campaigns found for selected date range</p>
          ) : (
            <div className="promo-analytics__table-container">
              <table className="promo-analytics__table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Total Codes</th>
                    <th>Redemptions</th>
                    <th>Conversion Rate</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map(campaign => (
                    <React.Fragment key={campaign.name}>
                      <tr className="promo-analytics__row">
                        <td className="promo-analytics__campaign-name">{campaign.name}</td>
                        <td>{campaign.totalCodes}</td>
                        <td>{campaign.totalRedemptions}</td>
                        <td>
                          <span className="promo-analytics__conversion-rate">
                            {campaign.conversionRate}%
                          </span>
                        </td>
                        <td>
                          <button
                            className="admin-btn admin-btn--sm admin-btn--secondary"
                            onClick={() => setExpandedCampaign(
                              expandedCampaign === campaign.name ? null : campaign.name
                            )}
                          >
                            {expandedCampaign === campaign.name ? 'Hide' : 'Details'}
                          </button>
                        </td>
                      </tr>
                      {expandedCampaign === campaign.name && (
                        <tr className="promo-analytics__detail-row">
                          <td colSpan="5">
                            <div className="promo-analytics__detail-content">
                              <h4>Campaign Codes</h4>
                              <div className="promo-analytics__codes-list">
                                {campaign.codes.slice(0, 10).map(code => (
                                  <div key={code.code} className="promo-analytics__code-item">
                                    <span className="promo-analytics__code">{code.code}</span>
                                    <span className="promo-analytics__code-usage">
                                      {code.usageCount || 0} uses
                                    </span>
                                  </div>
                                ))}
                                {campaign.codes.length > 10 && (
                                  <p className="promo-analytics__more">
                                    ... and {campaign.codes.length - 10} more codes
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderChannelPerformance = () => {
    const channels = getChannelMetrics();
    const topChannel = channels[0];

    // Prepare chart data
    const chartData = {
      labels: channels.map(c => c.name),
      datasets: [{
        label: 'Conversion Rate (%)',
        data: channels.map(c => parseFloat(c.conversionRate)),
        backgroundColor: 'rgba(0, 102, 255, 0.6)',
        borderColor: 'rgba(0, 102, 255, 1)',
        borderWidth: 1
      }]
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        title: {
          display: true,
          text: 'Conversion Rate by Channel'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Conversion Rate (%)'
          }
        }
      }
    };

    return (
      <div className="promo-analytics__channels">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Channel Performance</h3>
          </div>

          {channels.length === 0 ? (
            <p className="promo-analytics__empty">No channel data available</p>
          ) : (
            <>
              <div className="promo-analytics__table-container">
                <table className="promo-analytics__table">
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th>Total Codes</th>
                      <th>Redemptions</th>
                      <th>Conversion Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {channels.map(channel => (
                      <tr key={channel.name} className="promo-analytics__row">
                        <td>
                          <span className="promo-analytics__channel-name">
                            {channel.name}
                            {channel === topChannel && (
                              <span className="promo-analytics__top-badge">🏆 Top</span>
                            )}
                          </span>
                        </td>
                        <td>{channel.totalCodes}</td>
                        <td>{channel.totalRedemptions}</td>
                        <td>
                          <span className="promo-analytics__conversion-rate">
                            {channel.conversionRate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="promo-analytics__chart-container">
                <Bar data={chartData} options={chartOptions} />
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderABTests = () => {
    const abTests = getABTestMetrics();

    return (
      <div className="promo-analytics__ab-tests">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">A/B Test Results</h3>
          </div>

          {abTests.length === 0 ? (
            <p className="promo-analytics__empty">No A/B test data available</p>
          ) : (
            <div className="promo-analytics__ab-list">
              {abTests.map(test => (
                <div key={test.campaignName} className="promo-analytics__ab-test">
                  <h4 className="promo-analytics__ab-campaign">{test.campaignName}</h4>
                  
                  <div className="promo-analytics__ab-variants">
                    {test.variants.map(variant => (
                      <div 
                        key={variant.name} 
                        className={`promo-analytics__variant ${
                          test.winningVariant?.name === variant.name ? 'promo-analytics__variant--winner' : ''
                        }`}
                      >
                        <div className="promo-analytics__variant-header">
                          <h5 className="promo-analytics__variant-name">
                            {variant.name}
                            {test.winningVariant?.name === variant.name && (
                              <span className="promo-analytics__winner-badge">🏆 Winner</span>
                            )}
                          </h5>
                        </div>
                        <div className="promo-analytics__variant-stats">
                          <div className="promo-analytics__stat">
                            <span className="promo-analytics__stat-label">Codes:</span>
                            <span className="promo-analytics__stat-value">{variant.totalCodes}</span>
                          </div>
                          <div className="promo-analytics__stat">
                            <span className="promo-analytics__stat-label">Redemptions:</span>
                            <span className="promo-analytics__stat-value">{variant.totalRedemptions}</span>
                          </div>
                          <div className="promo-analytics__stat">
                            <span className="promo-analytics__stat-label">Conversion:</span>
                            <span className="promo-analytics__stat-value promo-analytics__conversion-rate">
                              {variant.conversionRate}%
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="promo-analytics__ab-significance">
                    <span className="promo-analytics__significance-label">Statistical Significance:</span>
                    <span className={`promo-analytics__significance-value ${
                      test.significance === 'Significant' ? 'promo-analytics__significance--yes' :
                      test.significance === 'Not Significant' ? 'promo-analytics__significance--no' :
                      'promo-analytics__significance--insufficient'
                    }`}>
                      {test.significance}
                    </span>
                    {test.significance === 'Insufficient data' && (
                      <small className="promo-analytics__significance-note">
                        (Need at least 30 redemptions per variant)
                      </small>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAllCodes = () => {
    const filteredCodes = filterByDateRange(promoCodes);
    const codesWithMarketing = filteredCodes.filter(c => c.campaignName || c.channel || c.abTestGroup);

    return (
      <div className="promo-analytics__all-codes">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">All Marketing Codes ({codesWithMarketing.length})</h3>
          </div>

          {codesWithMarketing.length === 0 ? (
            <p className="promo-analytics__empty">No codes with marketing metadata found</p>
          ) : (
            <div className="promo-analytics__table-container">
              <table className="promo-analytics__table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Campaign</th>
                    <th>Channel</th>
                    <th>A/B Group</th>
                    <th>Type</th>
                    <th>Usage</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {codesWithMarketing.map(code => (
                    <tr key={code.code} className="promo-analytics__row">
                      <td className="promo-analytics__code-cell">{code.code}</td>
                      <td>{code.campaignName || '-'}</td>
                      <td>{code.channel || '-'}</td>
                      <td>{code.abTestGroup || '-'}</td>
                      <td>{code.promoType}</td>
                      <td>{code.usageCount || 0}</td>
                      <td>
                        <span className={`promo-analytics__status promo-analytics__status--${code.status}`}>
                          {code.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── New render functions ─────────────────────────────────────────────────

  const renderSubscriptionBreakdown = () => {
    if (analyticsLoading) return <div className="promo-analytics__loading">Loading subscription data...</div>;
    if (!subscriptionBreakdown) return (
      <div className="promo-analytics__empty">
        Subscription breakdown data unavailable. Deploy the promo-analytics-query Lambda and set VITE_ADMIN_PROMO_ANALYTICS_URL.
      </div>
    );

    const { subscriptionTypeBreakdown: breakdown, conversionFunnel, byCampaign } = subscriptionBreakdown;

    const pieData = {
      labels: ['Monthly', 'Annual', 'Promo', 'Free', 'Unknown'],
      datasets: [{
        data: [breakdown.monthly, breakdown.annual, breakdown.promo, breakdown.free, breakdown.unknown],
        backgroundColor: [
          'rgba(0, 102, 255, 0.7)',
          'rgba(0, 217, 181, 0.7)',
          'rgba(245, 158, 11, 0.7)',
          'rgba(107, 114, 128, 0.7)',
          'rgba(209, 213, 219, 0.7)'
        ],
        borderWidth: 1
      }]
    };

    const funnelTotal = conversionFunnel.redeemed || 1;

    return (
      <div className="promo-analytics__subscription-breakdown">
        <div className="promo-analytics__metrics-row">
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value">{conversionFunnel.redeemed}</div>
            <div className="promo-analytics__metric-label">Codes Redeemed</div>
          </div>
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value">{conversionFunnel.converted}</div>
            <div className="promo-analytics__metric-label">Subscriptions Activated</div>
          </div>
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value promo-analytics__conversion-rate">
              {conversionFunnel.conversionRate}%
            </div>
            <div className="promo-analytics__metric-label">Activation Rate</div>
          </div>
        </div>

        <div className="promo-analytics__two-col">
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Subscription Type Distribution</h3>
            </div>
            <div className="promo-analytics__pie-container">
              <Pie data={pieData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>

          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Conversion Funnel</h3>
            </div>
            <div className="promo-analytics__funnel">
              {[
                { label: 'Redeemed', value: conversionFunnel.redeemed, pct: 100 },
                { label: 'Subscribed', value: conversionFunnel.converted, pct: +((conversionFunnel.converted / funnelTotal) * 100).toFixed(1) }
              ].map(step => (
                <div key={step.label} className="promo-analytics__funnel-step">
                  <div className="promo-analytics__funnel-label">{step.label}</div>
                  <div className="promo-analytics__funnel-bar-wrap">
                    <div
                      className="promo-analytics__funnel-bar"
                      style={{ width: `${step.pct}%` }}
                    />
                  </div>
                  <div className="promo-analytics__funnel-value">{step.value} ({step.pct}%)</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {byCampaign && byCampaign.length > 0 && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Subscription Types by Campaign</h3>
            </div>
            <div className="promo-analytics__table-container">
              <table className="promo-analytics__table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Monthly</th>
                    <th>Annual</th>
                    <th>Promo</th>
                    <th>Free</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {byCampaign.map(c => (
                    <tr key={c.name} className="promo-analytics__row">
                      <td className="promo-analytics__campaign-name">{c.name}</td>
                      <td>{c.monthly}</td>
                      <td>{c.annual}</td>
                      <td>{c.promo}</td>
                      <td>{c.free}</td>
                      <td><strong>{c.total}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDowngradeAnalysis = () => {
    if (analyticsLoading) return <div className="promo-analytics__loading">Loading downgrade data...</div>;
    if (!downgradeAnalysis) return (
      <div className="promo-analytics__empty">
        Downgrade analysis unavailable. Deploy the promo-analytics-query Lambda and set VITE_ADMIN_PROMO_ANALYTICS_URL.
      </div>
    );

    const { summary, byPromoCode } = downgradeAnalysis;

    return (
      <div className="promo-analytics__downgrade">
        <div className="promo-analytics__metrics-row">
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value">{summary.totalPromoSubscriptions}</div>
            <div className="promo-analytics__metric-label">Promo Subscribers</div>
          </div>
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value promo-analytics__metric-value--warning">
              {summary.downgradeRate}%
            </div>
            <div className="promo-analytics__metric-label">Downgrade / Churn Rate</div>
          </div>
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value">
              {summary.avgDaysToDowngrade !== null ? `${summary.avgDaysToDowngrade}d` : '—'}
            </div>
            <div className="promo-analytics__metric-label">Avg Days to Downgrade</div>
          </div>
        </div>

        {byPromoCode && byPromoCode.length > 0 && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Downgrade Risk by Promo Code</h3>
            </div>
            <div className="promo-analytics__table-container">
              <table className="promo-analytics__table">
                <thead>
                  <tr>
                    <th>Promo Code</th>
                    <th>Type</th>
                    <th>Downgrades</th>
                  </tr>
                </thead>
                <tbody>
                  {byPromoCode.map(row => (
                    <tr key={row.promoCode} className="promo-analytics__row">
                      <td className="promo-analytics__code-cell">{row.promoCode}</td>
                      <td>{row.promoType || '—'}</td>
                      <td>
                        <span className={`promo-analytics__downgrade-count ${row.downgrades > 5 ? 'promo-analytics__downgrade-count--high' : ''}`}>
                          {row.downgrades}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {(!byPromoCode || byPromoCode.length === 0) && (
          <div className="admin-card">
            <p className="promo-analytics__empty">No downgrade events recorded yet.</p>
          </div>
        )}
      </div>
    );
  };

  const renderCampaignROI = () => {
    if (analyticsLoading) return <div className="promo-analytics__loading">Loading ROI data...</div>;
    if (!campaignROI) return (
      <div className="promo-analytics__empty">
        Campaign ROI unavailable. Deploy the promo-analytics-query Lambda and set VITE_ADMIN_PROMO_ANALYTICS_URL.
      </div>
    );

    const { byCampaign, byChannel, totals } = campaignROI;

    // Export to CSV
    const handleExport = () => {
      const rows = [
        ['Campaign', 'Conversions', 'Total Revenue', 'Avg Revenue/Conversion'],
        ...(byCampaign || []).map(c => [c.name, c.conversions, c.totalRevenue, c.avgRevenuePerConversion])
      ];
      const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `campaign-roi-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    };

    return (
      <div className="promo-analytics__roi">
        <div className="promo-analytics__metrics-row">
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value">{totals.totalConversions}</div>
            <div className="promo-analytics__metric-label">Total Conversions</div>
          </div>
          <div className="admin-card promo-analytics__metric-card">
            <div className="promo-analytics__metric-value promo-analytics__conversion-rate">
              ${totals.totalRevenue}
            </div>
            <div className="promo-analytics__metric-label">Total Revenue Attributed</div>
          </div>
          <div className="admin-card promo-analytics__metric-card--action">
            <button className="admin-btn admin-btn--secondary" onClick={handleExport}>
              📥 Export CSV
            </button>
          </div>
        </div>

        {byCampaign && byCampaign.length > 0 && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Revenue by Campaign</h3>
            </div>
            <div className="promo-analytics__table-container">
              <table className="promo-analytics__table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Conversions</th>
                    <th>Total Revenue</th>
                    <th>Avg / Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {byCampaign.map(c => (
                    <tr key={c.name} className="promo-analytics__row">
                      <td className="promo-analytics__campaign-name">{c.name}</td>
                      <td>{c.conversions}</td>
                      <td className="promo-analytics__conversion-rate">${c.totalRevenue}</td>
                      <td>${c.avgRevenuePerConversion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {byChannel && byChannel.length > 0 && (
          <div className="admin-card">
            <div className="admin-card-header">
              <h3 className="admin-card-title">Revenue by Channel</h3>
            </div>
            <div className="promo-analytics__chart-container">
              <Bar
                data={{
                  labels: byChannel.map(c => c.name),
                  datasets: [{
                    label: 'Revenue ($)',
                    data: byChannel.map(c => c.totalRevenue),
                    backgroundColor: 'rgba(0, 217, 181, 0.7)',
                    borderColor: 'rgba(0, 217, 181, 1)',
                    borderWidth: 1
                  }]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true, title: { display: true, text: 'Revenue ($)' } } }
                }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Access control check
  if (!isSuperAdmin) {
    return (
      <div className="promo-analytics">
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Access Denied</h3>
          </div>
          <p className="promo-analytics__access-denied">
            🔒 Analytics dashboard is only accessible to SuperAdmin users.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="promo-analytics">
        <div className="admin-card">
          <p className="promo-analytics__loading">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="promo-analytics">
        <div className="admin-card">
          <div className="admin-alert admin-alert--danger">
            <span>❌</span> {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="promo-analytics">
      <div className="admin-page-header">
        <h2 className="admin-page-header__title">Promo Code Analytics</h2>
        <div className="admin-page-header__actions">
          <button 
            className="admin-btn admin-btn--secondary"
            onClick={() => { fetchPromoCodes(); fetchAnalytics(); }}
            disabled={loading || analyticsLoading}
          >
            🔄 Refresh
          </button>
          {lastUpdate && (
            <span className="promo-analytics__last-update">
              Last updated: {lastUpdate.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="admin-tabs" role="tablist" aria-label="Analytics views">
        <button
          className={`admin-tab ${activeTab === 'overview' ? 'admin-tab--active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'overview'}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overview
        </button>
        <button
          className={`admin-tab ${activeTab === 'channels' ? 'admin-tab--active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'channels'}
          onClick={() => setActiveTab('channels')}
        >
          📈 Channels
        </button>
        <button
          className={`admin-tab ${activeTab === 'ab-tests' ? 'admin-tab--active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'ab-tests'}
          onClick={() => setActiveTab('ab-tests')}
        >
          🧪 A/B Tests
        </button>
        <button
          className={`admin-tab ${activeTab === 'subscription-breakdown' ? 'admin-tab--active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'subscription-breakdown'}
          onClick={() => setActiveTab('subscription-breakdown')}
        >
          💳 Subscriptions
        </button>
        <button
          className={`admin-tab ${activeTab === 'downgrade' ? 'admin-tab--active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'downgrade'}
          onClick={() => setActiveTab('downgrade')}
        >
          📉 Downgrades
        </button>
        <button
          className={`admin-tab ${activeTab === 'roi' ? 'admin-tab--active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'roi'}
          onClick={() => setActiveTab('roi')}
        >
          💰 Campaign ROI
        </button>
        <button
          className={`admin-tab ${activeTab === 'all-codes' ? 'admin-tab--active' : ''}`}
          role="tab"
          aria-selected={activeTab === 'all-codes'}
          onClick={() => setActiveTab('all-codes')}
        >
          🎟️ All Codes
        </button>
      </div>

      {/* Tab Content */}
      <div className="promo-analytics__content">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'channels' && renderChannelPerformance()}
        {activeTab === 'ab-tests' && renderABTests()}
        {activeTab === 'subscription-breakdown' && renderSubscriptionBreakdown()}
        {activeTab === 'downgrade' && renderDowngradeAnalysis()}
        {activeTab === 'roi' && renderCampaignROI()}
        {activeTab === 'all-codes' && renderAllCodes()}
      </div>
    </div>
  );
};

export default PromoCodeAnalytics;
